/**
 * Vault UI motion kit
 * - 의존성 없음 (Web Animations API). CRA + React 그대로 사용.
 * - 값은 Vault UI 디자인 시스템 tokens.json 의 duration / easing / distance 와 같다.
 * - prefers-reduced-motion 이면 모든 시간이 0 이 되어 최종 상태로 바로 간다.
 */

export const EASE = {
  out: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  in: 'cubic-bezier(0.4, 0, 1, 1)',
  inOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
  pop: 'cubic-bezier(0.34, 1.36, 0.64, 1)', // 캘린더 날짜 선택 전용
} as const;

export const DUR = {
  fast: 150,
  base: 240,
  exit: 160,
  enter: 420,
  indicator: 360,
  stagger: 40,
} as const;

export const RISE = { sm: 6, md: 12, month: 24 } as const;

const STAGGER_CAP = 12; // 12번째 이후는 같은 시점에 등장

const reduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** 모션 시간 래퍼: 모션 줄이기 설정이면 0 */
export const t = (ms: number) => (reduced() ? 0 : ms);

type Targets = Element | Element[] | NodeListOf<Element> | null | undefined;
const list = (el: Targets): HTMLElement[] =>
  !el ? [] : el instanceof Element ? [el as HTMLElement] : (Array.from(el) as HTMLElement[]);

const done = (anims: Animation[]) => Promise.all(anims.map((a) => a.finished)).then(() => undefined);

/**
 * 1) 카드·타일 순차 등장 (대시보드 타일, 보드 카드, 담당자 카드)
 * 끝나면 인라인 스타일을 남기지 않는다(fill: none) → hover transform 과 충돌 없음.
 */
export function reveal(el: Targets, opts: { y?: number; stagger?: number; delay?: number } = {}) {
  const { y = RISE.md, stagger = DUR.stagger, delay = 0 } = opts;
  return done(
    list(el).map((node, i) =>
      node.animate(
        [
          { opacity: 0, transform: `translateY(${y}px)` },
          { opacity: 1, transform: 'none' },
        ],
        {
          duration: t(DUR.enter),
          delay: t(delay + Math.min(i, STAGGER_CAP) * stagger),
          easing: EASE.out,
          fill: 'backwards',
        },
      ),
    ),
  );
}

/** 2) 작은 요소 등장 (표 행, 칩, 폼 필드, 페이지 헤더 오른쪽 버튼들) */
export const revealSmall = (el: Targets, delay = 0) =>
  reveal(el, { y: RISE.sm, stagger: 30, delay });

/** 3) 화면 나가기: 나갈 때는 짧고 빠르게. 끝난 뒤 라우팅을 바꾼다. */
export function exit(el: Targets) {
  return done(
    list(el).map((node, i) =>
      node.animate(
        [
          { opacity: 1, transform: 'none' },
          { opacity: 0, transform: `translateY(-${RISE.sm}px)` },
        ],
        { duration: t(DUR.exit), delay: t(Math.min(i, 6) * 12), easing: EASE.in, fill: 'forwards' },
      ),
    ),
  );
}

/**
 * 4) 좌측 메뉴 활성 막대
 * 막대(indicator)를 활성 메뉴 세로 중앙으로 옮기고, 이동 거리에 비례해 잠깐 늘어난다(최대 1.9배).
 */
export function moveIndicator(indicator: HTMLElement | null, activeLink: HTMLElement | null) {
  if (!indicator || !activeLink) return;
  const y = activeLink.offsetTop + (activeLink.offsetHeight - indicator.offsetHeight) / 2;
  const from = Number(indicator.dataset.y ?? y);
  indicator.dataset.y = String(y);
  const stretch = 1 + Math.min(Math.abs(y - from) / 34, 3) * 0.3;
  indicator.animate(
    [
      { transform: `translateY(${from}px) scaleY(1)` },
      { transform: `translateY(${(from + y) / 2}px) scaleY(${stretch})`, offset: 0.5 },
      { transform: `translateY(${y}px) scaleY(1)` },
    ],
    { duration: t(DUR.indicator), easing: EASE.inOut, fill: 'forwards' },
  );
}

/**
 * 5) 캘린더 월 이동
 * dir: 다음 달 1, 이전 달 -1. swap 안에서 날짜 칸을 다시 그린다.
 */
export async function slideMonth(grid: HTMLElement, title: HTMLElement | null, dir: 1 | -1, swap: () => void) {
  if (reduced()) return swap();
  await grid.animate(
    [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: `translateX(${-dir * RISE.month}px)` }],
    { duration: DUR.exit, easing: EASE.in, fill: 'forwards' },
  ).finished;
  swap();
  grid.getAnimations().forEach((a) => a.cancel());
  grid.animate(
    [{ opacity: 0, transform: `translateX(${dir * RISE.month}px)` }, { opacity: 1, transform: 'none' }],
    { duration: DUR.enter, easing: EASE.out },
  );
  title?.animate(
    [{ opacity: 0, transform: `translateY(${dir * RISE.sm}px)` }, { opacity: 1, transform: 'none' }],
    { duration: DUR.base + 80, easing: EASE.out },
  );
}

/** 6) 날짜 선택 숫자 톡: 캘린더에서만 쓴다 */
export const pop = (el: HTMLElement | null) =>
  el?.animate([{ transform: 'scale(0.8)' }, { transform: 'scale(1)' }], {
    duration: t(300),
    easing: EASE.pop,
  });

/** 7) 숫자 카운트업 (KPI 타일). format 으로 천 단위·단위 표기 */
export function countUp(
  el: HTMLElement | null,
  to: number,
  { from = 0, duration = 900, format = (n: number) => Math.round(n).toLocaleString('ko-KR') } = {},
) {
  if (!el) return;
  const d = t(duration);
  if (!d) {
    el.textContent = format(to);
    return;
  }
  const start = performance.now();
  const step = (now: number) => {
    const p = Math.min((now - start) / d, 1);
    const eased = 1 - Math.pow(1 - p, 3);
    el.textContent = format(from + (to - from) * eased);
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/** 8) 막대·진행률 채우기 (scaleX 또는 scaleY) */
export function grow(el: Targets, axis: 'x' | 'y' = 'y', delay = 0) {
  return done(
    list(el).map((node, i) => {
      node.style.transformOrigin = axis === 'y' ? '50% 100%' : '0 50%';
      return node.animate(
        [{ transform: `scale${axis.toUpperCase()}(0)` }, { transform: 'none' }],
        {
          duration: t(DUR.enter + 200),
          delay: t(delay + Math.min(i, STAGGER_CAP) * DUR.stagger),
          easing: EASE.out,
          fill: 'backwards',
        },
      );
    }),
  );
}

/**
 * 화면 진입 순서 (권장 조합)
 *   헤더 오른쪽 버튼 revealSmall → 카드 reveal(80ms 뒤) → 막대 grow(200ms 뒤) → 숫자 countUp
 *
 * React 예시
 *   const ref = useRef<HTMLDivElement>(null);
 *   useLayoutEffect(() => {
 *     const root = ref.current; if (!root) return;
 *     revealSmall(root.querySelectorAll('[data-head] > *'));
 *     reveal(root.querySelectorAll('[data-card]'), { delay: 80 });
 *     grow(root.querySelectorAll('[data-bar]'), 'y', 200);
 *   }, []);
 *
 * 쓰지 않는 것: 제목 글자 단위 등장, 페이지 전체 페이드, 무한 반복 애니메이션.
 */
